import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [serverError, setServerError] = useState('');
  const [isValidSession, setIsValidSession] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);

  useEffect(() => {
    // Supabase automatically handles the token from the URL hash
    // and fires an INITIAL_SESSION or PASSWORD_RECOVERY event
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsValidSession(true);
        setIsCheckingSession(false);
      } else if (event === 'SIGNED_IN' && session) {
        // Also valid if already signed in via the recovery link
        setIsValidSession(true);
        setIsCheckingSession(false);
      }
    });

    // Fallback: check for existing session after a short delay
    const timer = setTimeout(async () => {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (session) {
        setIsValidSession(true);
      }
      setIsCheckingSession(false);
    }, 1500);

    return () => {
      subscription?.unsubscribe();
      clearTimeout(timer);
    };
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (serverError) setServerError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setServerError('');

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const { error } = await supabase?.auth?.updateUser({
        password: formData?.password,
      });

      if (error) {
        setServerError(error?.message || 'Failed to update password. Please try again.');
      } else {
        setIsSuccess(true);
        // Auto-redirect to login after 3 seconds
        setTimeout(() => navigate('/login'), 3000);
      }
    } catch (err) {
      setServerError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isCheckingSession) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-3">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="font-body text-sm text-muted-foreground">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/home-page" className="inline-flex items-center space-x-3 mb-6">
            <img
              src="/assets/images/ChatGPT_Image_Feb_3_2026_04_58_59_PM-1770675660365.png"
              alt="Draggin Trailers Logo"
              className="w-12 h-12 object-contain rounded-md"
            />
            <span className="font-heading font-bold text-2xl text-foreground">Draggin Trailers</span>
          </Link>
          <h1 className="font-heading text-3xl font-bold text-foreground mb-2">Reset Password</h1>
          <p className="font-body text-muted-foreground">Enter your new password below</p>
        </div>

        <div className="bg-card rounded-lg shadow-elevation-3 p-8">
          {!isValidSession ? (
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center">
                  <Icon name="LinkOff" size={32} color="var(--color-error)" strokeWidth={1.5} />
                </div>
              </div>
              <h2 className="font-heading text-xl font-semibold text-foreground">Invalid or Expired Link</h2>
              <p className="font-body text-sm text-muted-foreground">
                This password reset link is invalid or has expired. Please request a new one.
              </p>
              <Link to="/forgot-password">
                <Button fullWidth iconName="RefreshCw">
                  Request New Reset Link
                </Button>
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center space-x-1 font-body text-sm text-primary hover:underline"
              >
                <Icon name="ArrowLeft" size={14} />
                <span>Back to Sign In</span>
              </Link>
            </div>
          ) : isSuccess ? (
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center">
                  <Icon name="CheckCircle" size={32} color="var(--color-success)" strokeWidth={1.5} />
                </div>
              </div>
              <h2 className="font-heading text-xl font-semibold text-foreground">Password Updated!</h2>
              <p className="font-body text-sm text-muted-foreground">
                Your password has been successfully updated. You'll be redirected to sign in shortly.
              </p>
              <Link to="/login">
                <Button fullWidth iconName="LogIn">
                  Sign In Now
                </Button>
              </Link>
            </div>
          ) : (
            <>
              {serverError && (
                <div className="mb-6 p-4 bg-error/10 border border-error rounded-md flex items-start space-x-3">
                  <Icon name="AlertCircle" size={20} color="var(--color-error)" strokeWidth={2} />
                  <p className="font-body text-sm text-error flex-1">{serverError}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <Input
                  type="password"
                  name="password"
                  label="New Password"
                  placeholder="At least 8 characters"
                  value={formData?.password}
                  onChange={handleChange}
                  error={errors?.password}
                  required
                />

                <Input
                  type="password"
                  name="confirmPassword"
                  label="Confirm New Password"
                  placeholder="Re-enter your new password"
                  value={formData?.confirmPassword}
                  onChange={handleChange}
                  error={errors?.confirmPassword}
                  required
                />

                <Button
                  type="submit"
                  fullWidth
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  iconName="Lock"
                >
                  {isSubmitting ? 'Updating...' : 'Update Password'}
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-border text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center space-x-1 font-body text-sm text-primary hover:underline"
                >
                  <Icon name="ArrowLeft" size={14} />
                  <span>Back to Sign In</span>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
